import type { Player, Plugin, AdBreaksManager } from '@dicetechnology/vesper-sdk-web';
import { AdBreaksManagerEvents, AutoplayOptions, EventTypes } from '@dicetechnology/vesper-sdk-web';
import { useQuery } from '@tanstack/react-query';

import sdk from './sdk';

import EventBusInstance from '@core/eventbus';
import { VideoInteractionType } from '@shared-types/uefatv.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

const createPlayerManager = async (parent: HTMLElement | string) => {
  const playerManager = await sdk.createPlayerManager({
    parent,
    playerConfig: {
      defaultAutoplay: AutoplayOptions.ANY,
    },
  });

  const adBreaksManager = playerManager.getAdBreaksManager();

  playerManager.getPlayer().addPlugin({
    Plugin: TrackingPlugin,
    name: 'analytics',
    config: { adBreaksManager },
  });

  return playerManager;
};

export const usePlayerManager = (parent: string) =>
  useQuery({
    queryKey: ['vesper-player', parent],
    queryFn: () => createPlayerManager(parent),
  });

class TrackingPlugin implements Plugin {
  private player: Player;
  private adBreaksRunnging: boolean;
  private adBreaksManager: AdBreaksManager;

  constructor(player: Player, config: unknown) {
    this.player = player;
    this.adBreaksManager = (config as { adBreaksManager: AdBreaksManager }).adBreaksManager;
    this.adBreaksRunnging = false;
    this.attachListeners();
  }

  private attachListeners(): void {
    const milestones = [20, 40, 60, 80];
    let lastWarnedMilestone = -1;

    //This triggers when the ad starts and the ad stops, but i can't see how i can use this to stop the pause, end, play events
    this.adBreaksManager.on(AdBreaksManagerEvents.IS_PLAYING_BREAK_CHANGED, (event, data) => {
      this.adBreaksRunnging = data.isPlayingBreak;
    });

    this.player.on([EventTypes.PAUSE, EventTypes.PLAY, EventTypes.ENDED, EventTypes.FULLSCREEN_CHANGE], (event) => {
      if (this.adBreaksRunnging) return;

      this.track(`video_${event}`);
    });

    this.player.on(EventTypes.TIME_UPDATE, (event, data) => {
      const { currentTime } = data;
      const duration = this.player.getDuration();
      const percentage = (currentTime / duration) * 100;
      const milestone = milestones.reduce((max, m) => (percentage >= m ? m : max), 0);
      if (milestone && milestone !== lastWarnedMilestone) {
        this.track('video_progress', `${milestone}%`);
        lastWarnedMilestone = milestone;
      }
    });
  }

  private track(eventName: string, videoPercent?: string): void {
    const rootElement = this.player.getRootElement();
    const metaData = this.player.getMediaInfo()?.metadata;
    const title = metaData?.title;
    const videoId = rootElement?.getAttribute('data-id');
    const duration = this.player.getDuration();
    const fullscreen = this.player.getIsFullscreenActive();
    const currentTime = this.player.getCurrentTime();
    const { videoWidth: width, videoHeight: height } = this.player.getDimensions() || {};
    let videoInteraction = VideoInteractionType.MANUAL;

    const eventActions = {
      video_pause: () => {
        if (currentTime < 1) {
          videoInteraction = VideoInteractionType.AUTO;
        }
        if (this.player.getHasEnded()) return false;
      },
      video_progress: () => {
        if (!this.player.getIsPlaying()) return false;
        videoInteraction = VideoInteractionType.AUTO;
      },
      video_play: () => {
        if (currentTime === 0) {
          eventName = 'video_start';
          videoInteraction = VideoInteractionType.AUTO;
        } else {
          if (currentTime < 1) {
            videoInteraction = VideoInteractionType.AUTO;
          }
          eventName = 'video_resume';
        }
      },
      video_ended: () => {
        eventName = 'video_complete';
        videoPercent = '100%';
        videoInteraction = VideoInteractionType.AUTO;
      },
      video_fullscreenchange: () => {
        EventBusInstance.dispatch('uefatv-fullscreen', fullscreen);
        if (currentTime === 0) return false;
        eventName = fullscreen ? 'video_fullscreen_on' : 'video_fullscreen_off';
      },
    };

    if (eventActions[eventName]) {
      if (eventActions[eventName]() === false) return;
    }

    GoogleTagManagerHelper._push({
      event: eventName,
      videoType: 'uefatv',
      videoId: videoId,
      videoTitle: title,
      videoLength: Math.floor(duration),
      videoURL: window.location.href,
      videoPercent,
      videoPlayer: 'uefatv',
      videoWidth: width > 0 ? width : null,
      videoHeight: height > 0 ? height : null,
      videoInteraction,
      videoFullScreen: fullscreen,
      aspectRatio: width === 0 ? null : width === height ? '1:1' : '16:9',
      currentTime,
      streamingType: 'VOD',
      watchTimeInSeconds: Math.floor(currentTime),
    });
  }
}
